import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange } from '@/lib/firebase';
import { api } from '@/lib/api';

interface UserData {
  id: string;
  email: string;
  name?: string;
  whatsapp?: string;
  credits: number;
  freeGenerationsUsed: number;
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  emailVerified: boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  loading: true,
  emailVerified: false,
  refreshUserData: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const emailVerified = user?.emailVerified ?? false;

  const fetchUserData = useCallback(async () => {
    try {
      const data = await api.get<UserData>('/api/users/me');
      setUserData(data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      setUser(user);
      setLoading(false);

      if (user && user.emailVerified) {
        fetchUserData();
      } else {
        setUserData(null);
      }
    });

    return unsubscribe;
  }, [fetchUserData]);

  return (
    <AuthContext.Provider value={{ user, userData, loading, emailVerified, refreshUserData: fetchUserData }}>
      {children}
    </AuthContext.Provider>
  );
}
